import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Edit3, CreditCard, Building2, User, Wallet, History, Info, ShieldCheck, Activity } from 'lucide-react';
import { useAccountsDetail } from './hooks/useAccountsDetail';
import { formatBillionCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';

export function AccountsDetailPage() {
  const navigate = useNavigate();
  const { account, isLoading } = useAccountsDetail();

  if (isLoading) {
    return (
      <div className="w-full flex-1 min-w-0 animate-pulse space-y-4">
        <div className="h-16 rounded-md bg-white border border-slate-100 shadow-sm" />
        <div className="grid gap-4 md:grid-cols-12">
          <div className="md:col-span-8 h-96 rounded-md bg-white border border-slate-100 shadow-sm" />
          <div className="md:col-span-4 h-64 rounded-md bg-white border border-slate-100 shadow-sm" />
        </div>
      </div>
    );
  }

  if (!account) return <div className="p-10 text-center">Không tìm thấy tài khoản</div>;

  return (
    <div className="w-full flex-1 min-w-0 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('?tab=account')}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 active:scale-95 transition-all"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-slate-900">{account.accountName}</h1>
              {account.isDefault && <ShieldCheck className="h-4 w-4 text-emerald-600" />}
            </div>
            <p className="text-sm text-slate-500">{account.bankName} • {account.accountNumber}</p>
          </div>
        </div>
        <button
          onClick={() => navigate(`?tab=account-edit&id=${account.id}`)}
          className="flex h-10 items-center gap-2 rounded-md bg-emerald-700 px-4 text-sm font-bold text-white shadow-md shadow-emerald-700/10 hover:bg-emerald-800 transition-all active:scale-95"
        >
          <Edit3 className="h-4 w-4" />
          <span>Chỉnh sửa</span>
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-12">
        {/* Main Content */}
        <div className="md:col-span-8 space-y-4">
          {/* Summary Cards */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Số dư hiện tại</span>
                <Wallet className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-extrabold text-slate-900">{formatBillionCurrency(account.currentBalance)}</span>
                <span className="text-xs font-bold text-slate-400">{account.currency}</span>
              </div>
            </div>
            <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Số dư ban đầu</span>
                <Activity className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-extrabold text-slate-900">{formatBillionCurrency(account.initialBalance)}</span>
                <span className="text-xs font-bold text-slate-400">{account.currency}</span>
              </div>
            </div>
          </div>

          {/* Details Block */}
          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-6">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-bold">
              <Info className="h-4 w-4 text-emerald-600" />
              <span>Thông tin chi tiết</span>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Tên tài khoản</span>
                <p className="text-sm font-bold text-slate-700">{account.accountName}</p>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Ngân hàng</span>
                <p className="text-sm font-bold text-slate-700">{account.bankName}</p>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Số tài khoản</span>
                <p className="text-sm font-mono font-bold text-slate-700 tracking-wider">{account.accountNumber}</p>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Chủ tài khoản</span>
                <p className="text-sm font-bold text-slate-700 uppercase">{account.accountHolder}</p>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Ghi chú</span>
                <p className="text-sm text-slate-600 italic">{account.description || 'Không có ghi chú'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="md:col-span-4 space-y-4">
          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Trạng thái</h3>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-500">Trạng thái hiện tại</span>
              <span className={cn(
                'rounded px-2 py-0.5 text-[10px] font-bold uppercase',
                account.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
              )}>
                {account.status === 'active' ? 'Hoạt động' : 'Tạm dừng'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-500">Mặc định</span>
              <span className="text-xs font-bold text-slate-700">{account.isDefault ? 'Có' : 'Không'}</span>
            </div>
          </div>

          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Thông tin hệ thống</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">Ngày tạo</span>
                <span className="text-xs font-bold text-slate-700">{new Date(account.createdAt).toLocaleDateString('vi-VN')}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">Cập nhật cuối</span>
                <span className="text-xs font-bold text-slate-700">{new Date(account.updatedAt).toLocaleDateString('vi-VN')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
